import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, onSnapshot, addDoc, updateDoc, serverTimestamp, getDocs, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { FaFile } from 'react-icons/fa';
import { createNotification, NotificationType } from '../../services/NotificationService';
import { toast } from 'react-hot-toast';

const AdminRequestDetail = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);
  const [response] = useState('');
  const navigate = useNavigate();
  const [editors, setEditors] = useState([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const requestDoc = await getDoc(doc(db, 'requests', requestId));
        if (requestDoc.exists()) {
          setRequest({ id: requestDoc.id, ...requestDoc.data() });
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching request:', error);
        setLoading(false);
      }
    };

    const messagesQuery = query(
      collection(db, 'requests', requestId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(newMessages);
    });

    fetchRequest();
    return () => unsubscribe();
  }, [requestId]);

  useEffect(() => {
    if (request) {
      setStatus(request.status);
    }
  }, [request]);

  useEffect(() => {
    // Editörleri getir
    const fetchEditors = async () => {
      try {
        const editorsQuery = query(
          collection(db, 'users'),
          where('role', 'in', ['admin', 'editor'])
        );
        const snapshot = await getDocs(editorsQuery);
        const editorsList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().displayName || doc.data().email,
          role: doc.data().role
        }));
        setEditors(editorsList);
      } catch (error) {
        console.error('Editörler yüklenirken hata:', error);
      }
    };

    fetchEditors();
  }, []);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    console.log('Seçilen dosyalar:', selectedFiles);
    setFiles(selectedFiles);
  };

  const uploadFiles = async () => {
    try {
      const uploadedFiles = [];
      setUploadProgress(0);
      setUploadError(null);
      
      for (const file of files) {
        console.log('Dosya yükleniyor:', file.name);
        
        const reader = new FileReader();
        const filePromise = new Promise((resolve, reject) => {
          reader.onload = async () => {
            try {
              console.log('Dosya okundu, base64 dönüşümü yapılıyor');
              const base64String = reader.result;
              
              resolve({
                name: file.name,
                base64: base64String,
                type: file.type,
                size: file.size,
                uploadedBy: user.uid,
                uploadedAt: new Date().toISOString()
              });
            } catch (error) {
              console.error('Dosya dönüştürme hatası:', error);
              reject(error);
            }
          };
          
          reader.onerror = (error) => {
            console.error('Dosya okuma hatası:', error);
            reject(error);
          };
        });

        reader.readAsDataURL(file);
        try {
          const uploadedFile = await filePromise;
          uploadedFiles.push(uploadedFile);
          setUploadProgress((prev) => prev + (100 / files.length));
        } catch (error) {
          console.error(`Dosya işleme hatası (${file.name}):`, error);
          setUploadError('Dosya işleme sırasında bir hata oluştu');
          throw error;
        }
      }
      
      console.log('Tüm dosyalar işlendi:', uploadedFiles);
      setUploadProgress(100);
      return uploadedFiles;
    } catch (error) {
      console.error('Toplu dosya işleme hatası:', error);
      setUploadError('Dosya işleme sırasında bir hata oluştu');
      throw error;
    }
  };

  const handleSendMessage = async (e) => {
    if (e) {
      e.preventDefault();
    }
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      
      let uploadedFiles = [];
      if (files.length > 0) {
        uploadedFiles = await uploadFiles();
      }

      const messageData = {
        content: newMessage,
        senderId: user.uid,
        senderName: user.displayName || user.email,
        timestamp: serverTimestamp(),
        attachments: uploadedFiles
      };

      const messagesRef = collection(db, 'requests', requestId, 'messages');
      const messageRef = await addDoc(messagesRef, messageData);

      // Mesaj gönderildikten sonra input'u temizle ve state'i güncelle
      setNewMessage('');
      setFiles([]);
      setUploadProgress(0);
      
      // Bildirim gönder
      await createNotification(request.userId, NotificationType.REQUEST_MESSAGE_RECEIVED, {
        requestId,
        requestTitle: request.title,
        messagePreview: newMessage.trim().substring(0, 50) + (newMessage.length > 50 ? '...' : ''),
        senderName: user.displayName || user.email,
        senderId: user.uid,
        messageId: messageRef.id,
        role: user.role
      });

      scrollToBottom();
    } catch (error) {
      console.error('Mesaj gönderme hatası:', error);
      setUploadError('Mesaj gönderme sırasında bir hata oluştu');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Eğer istek zaten tamamlandıysa veya kilitliyse, durum değişikliğine izin verme
      if (request.status === 'completed' || request.isLocked) {
        toast.error('Tamamlanmış isteklerin durumu değiştirilemez.');
        return;
      }

      await updateDoc(doc(db, 'requests', requestId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: {
          id: user.uid,
          name: user.displayName || user.email,
          role: user.role,
          timestamp: serverTimestamp()
        }
      });

      // Kullanıcıya bildirim gönder
      await createNotification(request.userId, NotificationType.REQUEST_STATUS_CHANGED, {
        requestId,
        requestTitle: request.title,
        oldStatus: status,
        newStatus: newStatus,
        updatedBy: user.displayName || user.email,
        role: user.role
      });

      setStatus(newStatus);
      toast.success('İstek durumu güncellendi.');
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error('Durum güncellenirken bir hata oluştu.');
    }
  };

  const handleResponse = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        response: response,
        status: 'responded',
        respondedAt: serverTimestamp(),
        respondedBy: user.uid
      });

      // İstek sahibine bildirim gönder
      await createNotification(request.userId, NotificationType.REQUEST_RESPONDED, {
        requestId,
        requestTitle: request.title,
        respondedBy: user.displayName || user.email
      });

      navigate('/admin/requests');
    } catch (err) {
      console.error('Yanıt gönderme hatası:', err);
      setError('Yanıt gönderilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      // İsteği tamamlandı olarak işaretle
      await updateDoc(doc(db, 'requests', requestId), {
        status: 'completed',
        isLocked: true,
        completedAt: serverTimestamp(),
        completedBy: {
          id: user.uid,
          name: user.displayName || user.email,
          role: user.role
        },
        lastUpdatedBy: {
          id: user.uid,
          name: user.displayName || user.email,
          role: user.role,
          timestamp: serverTimestamp()
        }
      });

      // İstek sahibine bildirim gönder
      await createNotification(request.userId, NotificationType.REQUEST_COMPLETED, {
        requestId,
        requestTitle: request.title,
        completedBy: user.displayName || user.email
      });

      // Diğer admin ve editörlere bildirim gönder
      const usersRef = collection(db, 'users');
      const adminQuery = query(usersRef, where('role', 'in', ['admin', 'editor']));
      const adminSnapshot = await getDocs(adminQuery);
      
      for (const adminDoc of adminSnapshot.docs) {
        if (adminDoc.id !== user.uid) {
          await createNotification(adminDoc.id, NotificationType.REQUEST_COMPLETED, {
            requestId,
            requestTitle: request.title,
            completedBy: user.displayName || user.email
          });
        }
      }

      // UI'ı güncelle
      setRequest(prev => ({
        ...prev,
        status: 'completed',
        isLocked: true,
        completedAt: new Date(),
        completedBy: {
          id: user.uid,
          name: user.displayName || user.email,
          role: user.role
        }
      }));

      toast.success('İstek başarıyla tamamlandı ve kilitlendi.');
    } catch (error) {
      console.error('İstek tamamlanırken hata:', error);
      toast.error('İstek tamamlanırken bir hata oluştu.');
    }
  };

  // Tamamlamayı geri alma fonksiyonu (sadece adminler için)
  const handleUncomplete = async () => {
    if (user.role !== 'admin') {
      toast.error('Sadece adminler tamamlanmış istekleri geri alabilir.');
      return;
    }

    try {
      await updateDoc(doc(db, 'requests', requestId), {
        status: 'in_progress',
        isLocked: false,
        completedAt: null,
        completedBy: null,
        lastUpdatedBy: {
          id: user.uid,
          name: user.displayName || user.email,
          role: user.role,
          timestamp: serverTimestamp()
        }
      });

      // UI'ı güncelle
      setRequest(prev => ({
        ...prev,
        status: 'in_progress',
        isLocked: false,
        completedAt: null,
        completedBy: null
      }));

      toast.success('İsteğin tamamlanma durumu başarıyla geri alındı.');
    } catch (error) {
      console.error('İstek tamamlanması geri alınırken hata:', error);
      toast.error('İşlem sırasında bir hata oluştu.');
    }
  };

  const handleAssignTo = async (editorId) => {
    try {
      const selectedEditor = editors.find(e => e.id === editorId);
      if (!selectedEditor) return;

      await updateDoc(doc(db, 'requests', requestId), {
        assignedTo: {
          id: selectedEditor.id,
          name: selectedEditor.name,
          role: selectedEditor.role,
          assignedAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });

      // Atanan editöre bildirim gönder
      await addDoc(collection(db, 'notifications'), {
        userId: selectedEditor.id,
        type: 'request_assigned',
        title: 'Yeni Talep Ataması',
        content: `"${request.title}" başlıklı talep size atandı`,
        requestId,
        createdAt: serverTimestamp(),
        read: false
      });

      setRequest(prev => ({
        ...prev,
        assignedTo: {
          id: selectedEditor.id,
          name: selectedEditor.name,
          role: selectedEditor.role,
          assignedAt: new Date()
        }
      }));
    } catch (error) {
      console.error('Talep atama hatası:', error);
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await updateDoc(doc(db, 'requests', requestId), {
        priority,
        updatedAt: serverTimestamp()
      });

      // Kullanıcıya bildirim gönder
      await addDoc(collection(db, 'notifications'), {
        userId: request.userId,
        type: 'priority_change',
        title: 'Talep Önceliği Güncellendi',
        content: `Talebinizin önceliği "${priority === 'high' ? 'Yüksek' : 
                  priority === 'medium' ? 'Orta' : 'Düşük'}" olarak güncellendi`,
        requestId,
        createdAt: serverTimestamp(),
        read: false
      });

      setRequest(prev => ({
        ...prev,
        priority
      }));
    } catch (error) {
      console.error('Öncelik güncelleme hatası:', error);
    }
  };

  const handleEstimatedDateChange = async (date) => {
    try {
      await updateDoc(doc(db, 'requests', requestId), {
        estimatedCompletionDate: date,
        updatedAt: serverTimestamp()
      });

      // Kullanıcıya bildirim gönder
      await addDoc(collection(db, 'notifications'), {
        userId: request.userId,
        type: 'estimated_date_change',
        title: 'Tahmini Tamamlanma Tarihi Güncellendi',
        content: `Talebinizin tahmini tamamlanma tarihi ${new Date(date).toLocaleDateString('tr-TR')} olarak güncellendi`,
        requestId,
        createdAt: serverTimestamp(),
        read: false
      });

      setRequest(prev => ({
        ...prev,
        estimatedCompletionDate: date
      }));
    } catch (error) {
      console.error('Tahmini tarih güncelleme hatası:', error);
    }
  };

  const handleInternalNotesChange = async (notes) => {
    try {
      await updateDoc(doc(db, 'requests', requestId), {
        internalNotes: notes,
        updatedAt: serverTimestamp()
      });

      setRequest(prev => ({
        ...prev,
        internalNotes: notes
      }));
    } catch (error) {
      console.error('İç notlar güncelleme hatası:', error);
    }
  };

  const handleCategoryChange = async (category) => {
    try {
      await updateDoc(doc(db, 'requests', requestId), {
        category,
        updatedAt: serverTimestamp()
      });

      // Kullanıcıya bildirim gönder
      await addDoc(collection(db, 'notifications'), {
        userId: request.userId,
        type: 'category_change',
        title: 'Talep Kategorisi Güncellendi',
        content: `Talebinizin kategorisi "${category}" olarak güncellendi`,
        requestId,
        createdAt: serverTimestamp(),
        read: false
      });

      setRequest(prev => ({
        ...prev,
        category
      }));

      toast.success('Kategori başarıyla güncellendi.');
    } catch (error) {
      console.error('Kategori güncelleme hatası:', error);
      toast.error('Kategori güncellenirken bir hata oluştu.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Belirtilmemiş';
    
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleString('tr-TR');
      }
      
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString('tr-TR');
      }
      
      if (typeof timestamp === 'string') {
        return new Date(timestamp).toLocaleString('tr-TR');
      }
      
      return 'Geçersiz tarih';
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return 'Geçersiz tarih';
    }
  };

  if (loading) {
    return <div className="p-4">Yükleniyor...</div>;
  }

  if (!request) {
    return <div className="p-4">Talep bulunamadı.</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4">Talep Detayı</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-gray-600">Kullanıcı:</p>
            <p className="font-medium">{request.userEmail}</p>
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">Durum</p>
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="pending">Beklemede</option>
              <option value="in-progress">İşleniyor</option>
              <option value="completed">Tamamlandı</option>
            </select>
          </div>
          <div>
            <p className="text-gray-600">Oluşturulma Tarihi:</p>
            <p className="font-medium">{request.createdAt?.toDate().toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">Atanan Yetkili</p>
            <select
              value={request.assignedTo?.id || ''}
              onChange={(e) => handleAssignTo(e.target.value)}
              className="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="">Yetkili Seçin</option>
              {editors.map(editor => (
                <option key={editor.id} value={editor.id}>
                  {editor.name} ({editor.role === 'admin' ? 'Yönetici' : 'Editör'})
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">Öncelik</p>
            <select
              value={request.priority || 'normal'}
              onChange={(e) => handlePriorityChange(e.target.value)}
              className="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="low">Düşük</option>
              <option value="normal">Normal</option>
              <option value="high">Yüksek</option>
            </select>
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">Tahmini Tamamlanma</p>
            <input
              type="date"
              value={request.estimatedCompletionDate || ''}
              onChange={(e) => handleEstimatedDateChange(e.target.value)}
              className="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            />
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">İç Notlar</p>
            <textarea
              value={request.internalNotes || ''}
              onChange={(e) => handleInternalNotesChange(e.target.value)}
              placeholder="Sadece yönetici ve editörlerin görebileceği notlar..."
              className="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              rows="3"
            />
          </div>
          <div>
            <p className="text-gray-600 text-sm mb-1">Kategori</p>
            <select
              value={request.category || 'genel'}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="genel">Genel</option>
              <option value="excel">Excel</option>
              <option value="veri-analizi">Veri Analizi</option>
              <option value="makro">Makro</option>
              <option value="otomasyon">Otomasyon</option>
              <option value="raporlama">Raporlama</option>
              <option value="formul">Formül</option>
              <option value="pivot">Pivot Tablo</option>
              <option value="grafik">Grafik</option>
              <option value="vba">VBA</option>
              <option value="diger">Diğer</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-bold mb-6">Mesajlar</h3>
        <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.senderId === user.uid ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] rounded-lg p-4 ${
                message.senderId === user.uid
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{message.senderName || message.senderEmail}</span>
                  <span className="text-sm opacity-75">
                    {formatDate(message.createdAt)}
                  </span>
                </div>
                <div className="whitespace-pre-wrap">{message.content}</div>
                {message.files && message.files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {message.files.map((file, index) => (
                      <a
                        key={index}
                        href={file.base64}
                        download={file.name}
                        className={`flex items-center space-x-2 text-sm hover:underline ${
                          message.senderId === user.uid ? 'text-white' : 'text-blue-500'
                        }`}
                      >
                        <FaFile />
                        <span>{file.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {!request?.status === 'completed' && !request?.isLocked && (
          <form onSubmit={handleSendMessage} className="mt-4">
            <div className="space-y-4">
              <div className="relative">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (newMessage.trim()) {
                        handleSendMessage();
                      }
                    }
                  }}
                  disabled={sending}
                  placeholder="Mesajınızı yazın... (Göndermek için Enter, yeni satır için Shift + Enter)"
                  className="w-full p-3 border rounded-lg resize-none min-h-[120px] focus:outline-none focus:border-blue-500 focus:ring-0"
                  spellCheck="false"
                  autoComplete="off"
                />
                {sending && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <input
                  type="file"
                  onChange={handleFileChange}
                  multiple
                  className="file-input file-input-bordered flex-1"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] transition-colors duration-200"
                >
                  {sending ? 'Gönderiliyor...' : 'Gönder'}
                </button>
              </div>

              {files.length > 0 && (
                <div className="text-sm text-gray-600">
                  {files.length} dosya seçildi
                </div>
              )}

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}

              {uploadError && (
                <div className="text-red-500 text-sm">
                  {uploadError}
                </div>
              )}
            </div>
          </form>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex justify-end space-x-4 mt-6">
        {!request?.status === 'completed' && !request?.isLocked && (
          <>
            <button
              onClick={handleComplete}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              Tamamla ve Kilitle
            </button>
            <button
              onClick={handleResponse}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Yanıtla
            </button>
          </>
        )}
      </div>

      {user.role === 'admin' && (
        <div className="mt-4">
          {!request.isLocked ? (
            <button
              onClick={handleComplete}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Tamamla ve Kilitle
            </button>
          ) : (
            <button
              onClick={handleUncomplete}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
            >
              Tamamlamayı Geri Al
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminRequestDetail; 